import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { useUpdateDocument } from '../../components/DocumentContentDetails/hooks/useUpdateDocument';
import { useValidateData } from './hooks/useValidateData';

import SubmissionFlowHeader from '@common/components/SubmissionFlowHeader/SubmissionFlowHeader';
import SubmissionFlowFooter from '@common/components/SubmissionFlowFooter/SubmissionFlowFooter';
import { usePrepareContentDetailsForm } from '../../components/DocumentContentDetails/hooks/usePrepareContentDetailsForm';
import { ValidationError } from './ValidationError';
import { ContentDetailsFormField } from './types';
import { DocumentType } from '@common/types';

import * as Styled from './styles';
import Tabs from '@common/components/Tabs/Tabs';
import { DocumentContent } from '../../components/DocumentContentDetails/DocumentContent';
import { HeaderBreadcrumb, Link } from '@common/components/SubmissionFlowHeader/styles';
import { DocumentVersionHistory } from '../../components/DocumentVersionHistory/DocumentVersionHistory';
import { documentTypeToCustomLabelMap } from '@common/constants';
import { IDocumentDto } from '@common/features/submission/types';
import {
  DocumentDetailsReadonlySections,
  DocumentProcessingDetails,
  DocumentProcessingFlow,
} from '../../components/DocumentContentDetails/types';
import { useGetAlternativeTextFile } from '@common/hooks/useGetAlternativeTextFile';
import { getDocumentAttributes, getEmcShortLinkDetails } from '../../helpers';
import { useGeneratePrimaryFileDescription } from '../../components/DocumentContentDetails/hooks/useGenerateFileDescription';
import { useFetchDocumentHtmlContent } from '@common/hooks/useFetchDocumentHtmlContent';
import { formatDocumentSubTitle } from '@common/utils/utils';

import { useAppSelector } from '@common/hooks/redux';
import { selectConfiguration } from '@common/features/configuration/selectors';
import { useFeatureFlags } from '@common/hooks/useFeatureFlags';
import { useFetchCompanyConfiguration } from '@common/features/configuration/useFetchCompanyConfiguration';

export interface IDocumentDetailsContent {
  showHistoryFiltering: boolean;
  submissionId: number;
  companyId: number;
  documentId: string;
  linkedProductId?: number | undefined;
  contentType: DocumentType;
  contentTitle: string;
  originalFileName: string;
  secondaryOriginalFileName: string | undefined;
  url: string;
  productGroupName: string;
  version: number;
  documentReadonlySections?: DocumentDetailsReadonlySections;
  documentProcessingDetails: DocumentProcessingDetails;
  isFormExtended: boolean;
  documents: IDocumentDto[];
  isAdmin?: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  onFileDownload?: () => void;
  onSecondaryFileDownload?: () => void;
}

const DocumentDetailsContent = ({
  showHistoryFiltering,
  submissionId,
  companyId,
  documentId,
  linkedProductId,
  contentType,
  contentTitle,
  originalFileName,
  secondaryOriginalFileName,
  url,
  version,
  isFormExtended,
  documents,
  documentReadonlySections,
  documentProcessingDetails,
  productGroupName,
  isAdmin = false,
  onCancel,
  onSuccess,
  onFileDownload,
  onSecondaryFileDownload,
}: IDocumentDetailsContent) => {
  const [isValidationPopupOpen, setIsValidationPopupOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  const document = documents.find((d) => d.documentId === Number(documentId) && d.type === contentType);
  const attributes = getDocumentAttributes(contentType, isAdmin, documentProcessingDetails.documentProcessingFlow);
  const documentWithAttributes = { document, attributes };

  const fileAttributes = useGeneratePrimaryFileDescription(contentType, documentProcessingDetails.documentProcessingFlow);

  const { alternativeTextFileName, openAlternativeTextFile } = useGetAlternativeTextFile(
    submissionId.toString(),
    documentId,
    documentWithAttributes.attributes,
  );

  // for qrd Pils we would like to load details from epil as there may be different submission reasons on both of those types
  const documentTypeToLoad =
    contentType === DocumentType.Pil && documentProcessingDetails.documentProcessingFlow === DocumentProcessingFlow.qrdPil
      ? DocumentType.Epil
      : contentType;

  const { handleSubmit, methods } = usePrepareContentDetailsForm(
    documentTypeToLoad,
    documents,
    document?.documentId,
    documentProcessingDetails,
    '',
  );

  const shouldAlternativeTextFileBeIncluded =
    methods.watch(ContentDetailsFormField.hasAlternativeTextFile) && !documentReadonlySections?.hasAlternativeTextFile;

  useEffect(() => {
    if (alternativeTextFileName !== undefined) {
      methods.setValue(ContentDetailsFormField.hasAlternativeTextFile, true);
      methods.setValue(ContentDetailsFormField.alternativeTextFileName, alternativeTextFileName);
    }
  }, [alternativeTextFileName, methods]);

  const { data, documentFetching } = useFetchDocumentHtmlContent(
    attributes.hasHtmlContent
      ? {
          submissionId,
          documentId: Number(documentId),
          version,
          type: contentType,
        }
      : undefined,
    (html: string) => {
      methods.setValue(ContentDetailsFormField.htmlContent, html);
    },
  );

  // Boolean fields where both Yes and No are valid choices (only null/undefined is invalid)
  const booleanFieldsWithFalseValid: string[] = [];

  const excludedFieldsToValidate = [
    ContentDetailsFormField.details,
    ContentDetailsFormField.hasDigitalVersion,
    ContentDetailsFormField.documentTitle,
    ContentDetailsFormField.hasAlternativeTextFile,
    ContentDetailsFormField.alternativeTextFileName,
    ContentDetailsFormField.isCustomerApprovalRequired,
    ContentDetailsFormField.isVisibleToHCP,
    ContentDetailsFormField.replacementSecondaryFile,
    ContentDetailsFormField.replacementSecondaryFileName,
    ContentDetailsFormField.customerUrl,
  ];

  const noDatesDocumentTypes = [
    DocumentType.Rmm,
    DocumentType.Dhcp,
    DocumentType.UserManual,
    DocumentType.SafetyAlert,
    DocumentType.ProductInformation,
    DocumentType.AudioVideo,
    DocumentType.Audio,
    DocumentType.Video,
  ];

  const noAuthTypes = [DocumentType.AudioVideo, DocumentType.SafetyAlert, DocumentType.ProductInformation];

  const contentDescriptionTypes = [
    DocumentType.AudioVideo,
    DocumentType.Audio,
    DocumentType.Video,
    DocumentType.Rmm,
    DocumentType.Dhcp,
  ];

  const hasHtml = [ContentDetailsFormField.contentDescription];

  const { enableQrdXpil } = useFeatureFlags();

  const showAccessibilityServiceQuestion =
    enableQrdXpil && documentProcessingDetails.documentProcessingFlow === DocumentProcessingFlow.qrdPil;

  if (!shouldAlternativeTextFileBeIncluded) {
    excludedFieldsToValidate.push(ContentDetailsFormField.alternativeTextFile);
  }

  if (contentType !== DocumentType.Pil) {
    excludedFieldsToValidate.push(ContentDetailsFormField.isCustomerApprovalRequired);
  }

  if (noDatesDocumentTypes.includes(contentType)) {
    excludedFieldsToValidate.push(ContentDetailsFormField.authorisedDate);
    excludedFieldsToValidate.push(ContentDetailsFormField.regulatorApprovalDate);
  }

  if (!contentDescriptionTypes.includes(contentType)) {
    excludedFieldsToValidate.push(ContentDetailsFormField.contentDescription);
  }

  if (!(documentWithAttributes.attributes.allowAdminWeblinkUpload || ([DocumentType.Rmm].includes(contentType) && url))) {
    excludedFieldsToValidate.push(ContentDetailsFormField.webLink);
  }

  if (!documentWithAttributes.attributes.allowHtmlContentUpload) {
    excludedFieldsToValidate.push(ContentDetailsFormField.htmlContent);
  }

  if (noAuthTypes.includes(contentType)) {
    excludedFieldsToValidate.push(ContentDetailsFormField.authorisedDate);
    excludedFieldsToValidate.push(ContentDetailsFormField.regulatorApprovalDate);
    excludedFieldsToValidate.push(ContentDetailsFormField.approvedByRegulator);
    excludedFieldsToValidate.push(ContentDetailsFormField.isRegulatorApproved);
  }

  if (showAccessibilityServiceQuestion) {
    booleanFieldsWithFalseValid.push(ContentDetailsFormField.isAccessiblePilRequested);
  } else {
    excludedFieldsToValidate.push(ContentDetailsFormField.isAccessiblePilRequested);
  }

  const { validateData, clearValidationFields } = useValidateData(excludedFieldsToValidate, hasHtml, booleanFieldsWithFalseValid);

  const { updateDocument } = useUpdateDocument(
    submissionId,
    isFormExtended,
    contentType,
    documentWithAttributes.attributes,
    !documentReadonlySections?.hasAlternativeTextFile,
  );

  const handleSave = () => {
    handleSubmit((data) => {
      const validatedData = validateData(data);
      clearValidationFields(validatedData, !isFormExtended);

      if (validatedData.length) {
        setMissingFields(validatedData);
        return setIsValidationPopupOpen(true);
      }

      updateDocument(
        { 
          documentId: parseInt(documentId!), 
          documentType: contentType,
          ...data,
          isAccessiblePilRequested: showAccessibilityServiceQuestion ? data.isAccessiblePilRequested : false
        },
        {
          onSuccess: () => onSuccess(),
        },
      );
    })();
  };

  const handleValidatePopupCancel = () => {
    setIsValidationPopupOpen(false);
  };

  const getTabs = () => {
    const items = [
      {
        label: `${documentTypeToCustomLabelMap[contentType]} details`,
        content: (
          <DocumentContent
            documentTitle={contentTitle}
            documentReadonlySections={documentReadonlySections}
            version={version}
            fileName={originalFileName}
            url={url}
            customerUrl={documentProcessingDetails.customerUrl}
            htmlContent={data ?? ''}
            contentType={contentType}
            onFileOpen={onFileDownload}
            documentAttributes={documentWithAttributes!.attributes}
            documentProcessingFlow={documentProcessingDetails.documentProcessingFlow}
            secondaryFileName={secondaryOriginalFileName}
            onSecondaryFileOpen={onSecondaryFileDownload}
            onAlternativeTextFileNameClick={openAlternativeTextFile}
            fileDescriptions={fileAttributes}
            isAdmin={isAdmin}
            showAccessibilityServiceQuestion={showAccessibilityServiceQuestion}
          />
        ),
      },
      {
        label: `Version history`,
        content: (
          <DocumentVersionHistory
            isAdmin={isAdmin}
            documentId={documentWithAttributes.document?.documentId ?? 0}
            documentType={documentWithAttributes.document?.type}
            showHistoryFiltering={showHistoryFiltering}
          />
        ),
      },
    ];

    return items;
  };

  const renderContent = () => {
    switch (contentType) {
      case DocumentType.Smpc:
      case DocumentType.Pil:
      case DocumentType.Rmm:
      case DocumentType.Dhcp:
      case DocumentType.UserManual:
      case DocumentType.SafetyAlert:
      case DocumentType.ProductInformation:
      case DocumentType.AudioVideo:
      case DocumentType.Audio:
      case DocumentType.Video:
        return <Tabs items={getTabs()} />;
      default:
        return <>Work in progress</>;
    }
  };

  const configuration = useAppSelector(selectConfiguration);
  const { showQrCodeForPil } = useFeatureFlags();
  const { companyConfiguration } = useFetchCompanyConfiguration(companyId);

  const { emcShortLink, emcUrlTitle } = getEmcShortLinkDetails(
    attributes,
    linkedProductId,
    contentType,
    configuration.emcShortLink,
    companyConfiguration,
  );

  return (
    <>
      <Styled.ContentDetailsWrap>
        <FormProvider {...methods}>
          <SubmissionFlowHeader
            title={formatDocumentSubTitle(isAdmin, productGroupName, document, false)}
            onBackClick={onCancel}
            onCloseClick={onCancel}
            emcUrl={emcShortLink}
            showQrCode={showQrCodeForPil}
            emcUrlTitle={emcUrlTitle}
          >
            <HeaderBreadcrumb>
              <Link>Product Family</Link> &gt; <Link>Product Group</Link> &gt; Document details
            </HeaderBreadcrumb>
          </SubmissionFlowHeader>
          {!documentFetching && renderContent()}
          <ValidationError
            missingFields={missingFields}
            isOpen={isValidationPopupOpen}
            contentType={contentType!}
            handleClose={handleValidatePopupCancel}
            handleConfirm={handleValidatePopupCancel}
          />
        </FormProvider>
      </Styled.ContentDetailsWrap>
      <SubmissionFlowFooter
        onContinue={handleSave}
        continueText="Save changes"
        continueDisabled={
          !!methods.formState.errors[ContentDetailsFormField.htmlContent] ||
          !!methods.formState.errors[ContentDetailsFormField.details]
        }
        onCancel={onCancel}
      />
    </>
  );
};

export default DocumentDetailsContent;
